import { IDP_ROLES, IdpAuthError, IdpRole, IdpSession } from './provider';

/**
 * O unico lugar do app que conhece nome de claim do IDP.
 *
 * Os nomes estao no contrato (`seazone-idp/docs/integracao/claims.md`); se
 * mudarem, muda este arquivo e mais nada. O duble e o provider real passam os
 * dois por aqui, e e o que garante o mesmo shape de sessao.
 */

const PERMISSION_FLAT = /^[a-z0-9-]+:[a-z0-9-]+$/;

const knownRoles = new Set<string>(IDP_ROLES);

function isRole(value: unknown): value is IdpRole {
  return typeof value === 'string' && knownRoles.has(value);
}

function asText(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * O vinculo chega como numero (mapper `jsonType.label: "long"`), mas o atributo
 * do usuario no realm e texto (`"900001"`) e mapper mal configurado emitiria a
 * string. Aceita os dois e normaliza para numero: quem le isso compara
 * igualdade, nao faz aritmetica.
 */
function asSapronUserId(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) ? value : undefined;
  }
  if (typeof value === 'string' && /^\d+$/.test(value)) {
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) ? parsed : undefined;
  }
  return undefined;
}

export interface IdpClaims {
  readonly [claim: string]: unknown;
}

/**
 * Traduz as claims do token para a sessao do app.
 *
 * Claim desconhecida ou fora do formato e descartada em vez de derrubar o
 * login: role nova no IDP nao pode virar erro de autenticacao aqui. `sub` e
 * `exp` sao os dois sem os quais nao existe sessao.
 */
export function sessionFromClaims(claims: IdpClaims): IdpSession {
  const sub = asText(claims.sub);
  if (sub === undefined) {
    throw new IdpAuthError('claim_sub_ausente', 'Token do IDP sem a claim `sub`.');
  }
  if (typeof claims.exp !== 'number') {
    throw new IdpAuthError('claim_exp_ausente', 'Token do IDP sem a claim `exp`.');
  }

  const sapronUserId = asSapronUserId(claims.sapron_user_id);
  const roles = Array.isArray(claims.roles) ? claims.roles.filter(isRole) : [];
  const permissions = Array.isArray(claims.permissions)
    ? claims.permissions.filter(
        (permission): permission is string =>
          typeof permission === 'string' && PERMISSION_FLAT.test(permission),
      )
    : [];

  return {
    sub,
    ...(sapronUserId !== undefined && { sapronUserId }),
    name: asText(claims.name) ?? asText(claims.preferred_username) ?? sub,
    email: asText(claims.email) ?? '',
    roles,
    permissions,
    expiresAt: claims.exp,
  };
}
